import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Link, useHistory, useParams } from 'react-router-dom';
import { Autocomplete, Button, InputLabel, TextField } from '@mui/material';
import { ExpandMoreOutlined } from '@mui/icons-material';
import classNames from 'classnames';
import { LoadingButton } from '@mui/lab';
import urlJoin from 'url-join';
import { jwtScopes } from '../../../services/user-service';
import { getSelectedAccount } from '../../../helpers/composer-helper';
import { createOauthApplication, getOauthApplication } from '../../../services/oauth-application-service';
import styles from '../composer.module.css';

const ApiKeysEdit = () => {
  const history = useHistory();

  const /** @type {Object<string, string>} */ { oAuthApplicationId } = useParams();

  const account = useSelector((state) => getSelectedAccount(state));
  const { _id: accountId } = account;

  const [oAuthApplication, setOauthApplication] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const isNew = !oAuthApplicationId;
  const title = isNew ? 'Create API Key' : 'Edit API Key';
  const buttonLabel = isNew ? 'Create API Key' : 'Update API Key';

  useEffect(() => {
    if (oAuthApplicationId) {
      getOauthApplication(accountId, oAuthApplicationId).then((data) => {
        setOauthApplication((prevState) => {
          const newState = {
            ...prevState,
            ...data,
          };
          return newState;
        });
      });
    }
  }, [oAuthApplicationId]);

  const onOauthApplicationChange = (e) => {
    const { name, value } = e.target;

    setOauthApplication((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  const handleSelectChange = (field, value) => {
    setOauthApplication((prevState) => {
      return {
        ...prevState,
        [field]: value,
      };
    });
  };

  const saveOauthApplication = () => {
    if (!oAuthApplication) {
      return;
    }
    setLoading(true);
    setErrors({});

    createOauthApplication(accountId, oAuthApplication)
      .then((res) => {
        setLoading(false);
        history.push(urlJoin('/api-keys/', res._id));
      })
      .catch(({ response: { data: err } }) => {
        setLoading(false);
        setErrors(err);
      });
  };

  const cancel = () => {
    history.push('/api-keys');
  };

  return (
    <div className={styles.pageCenter}>
      <Link to="/api-keys">← Back</Link>
      <div className={styles.flex}>
        <h1 className={styles.h1}>{title}</h1>
      </div>
      <div className={styles.pageColumns}>
        <div className={styles.f1} style={{ marginBottom: '12em' }}>
          <div className={styles.inputBlock}>
            <InputLabel htmlFor="name" className={styles.inputLabel}>
              Name
            </InputLabel>
            <TextField
              value={oAuthApplication ? oAuthApplication.name || '' : ''}
              onChange={onOauthApplicationChange}
              error={Boolean(errors['/name'] || errors['/'])}
              helperText={errors['/name'] || errors['/']}
              id="name"
              name="name"
            />
          </div>
          <div className={styles.inputBlock}>
            <Autocomplete
              popupIcon={<ExpandMoreOutlined />}
              ChipProps={{ className: classNames(styles.chip), style: { marginTop: '0', marginRight: '6px' } }}
              value={oAuthApplication ? oAuthApplication.scopes || [] : []}
              multiple
              options={[jwtScopes.externalRead, jwtScopes.externalWrite]}
              getOptionLabel={(option) => {
                return option.replace('external-', '');
              }}
              id="scopes"
              renderInput={(p) => (
                <div>
                  <InputLabel htmlFor="name" className={styles.inputLabel}>
                    Permissions
                  </InputLabel>
                  <TextField {...p} error={Boolean(errors['/scopes'])} helperText={errors['/scopes']} />
                </div>
              )}
              fullWidth
              selectOnFocus
              disableClearable
              onChange={(event, selectedScopes) => {
                handleSelectChange('scopes', selectedScopes);
              }}
            />
          </div>

          <div className={classNames(styles.flex, styles.justifyEnd)}>
            <Button
              className={classNames(styles.secondaryButton)}
              onClick={() => {
                cancel();
              }}
            >
              Cancel
            </Button>
            <LoadingButton
              loading={loading}
              loadingPosition={loading ? 'start' : undefined}
              startIcon={<div />}
              className={classNames(styles.keyButton)}
              onClick={() => {
                saveOauthApplication();
              }}
            >
              {buttonLabel}
            </LoadingButton>
          </div>
        </div>
        <div style={{ flexBasis: '45%' }}>
          <div className={styles.f1} />
        </div>
      </div>
    </div>
  );
};

export default ApiKeysEdit;
